import { useState } from 'react';
import { Group, OrganisationResponse } from 'api/httpEntities';
import OrganisationOrGroupList from 'components/OrganisationOrGroupList';
import { Box, Container } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TextV2,
  PaginationControls,
  usePagination,
  Counter,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useTranslation } from 'react-i18next';
import SearchBox from 'components/OrganisationSearchBox';
import { isAdvancedUser } from 'utils/role';
import { User } from 'contexts/AuthContext';

type GroupOrganisationListProps = {
  organisations: OrganisationResponse[];
  group: Group;
  user?: User;
};

const GroupOrganisationList = ({
  organisations,
  group,
  user,
}: GroupOrganisationListProps) => {
  const isAU = isAdvancedUser(user?.role);
  const OrganisationLabels: { all: string; filtered: string } = {
    all: isAU
      ? 'ui.event-management.group.my-organisations'
      : 'ui.event-management.group.total-organisations',
    filtered: 'ui.event-management.group.search-results',
  };
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const isSearching = searchText !== '';
  const filteredOrgs = isSearching
    ? organisations.filter((organisation: OrganisationResponse) =>
        organisation.name.toLowerCase().includes(searchText.toLowerCase()),
      )
    : organisations;

  const title =
    searchText.length === 0
      ? t(OrganisationLabels.all)
      : t(OrganisationLabels.filtered);
  const displayCount = isSearching ? filteredOrgs.length : organisations.length;

  const hasOrgs = organisations.length > 0;

  const paginator = usePagination(filteredOrgs, 9);
  const { pagedItems, handlePageChange, pageCount } = paginator;

  const onSearchTextChanges = (value: string) => {
    setSearchText(value);
    handlePageChange(1);
  };

  return (
    <Container
      sx={(theme) => ({
        padding: `${theme.spacing(spacingSizeMap.M)} 0 ${theme.spacing(
          spacingSizeMap.XL,
        )}`,
      })}
    >
      <Box>
        <Grid
          container
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px',
          }}
        >
          <Grid size={{ xs: 12, md: 'auto' }}>
            <TextV2 component="h2" variant="h3">
              {title}
              <Counter
                count={displayCount}
                size="large"
                sx={{
                  marginLeft: 1,
                }}
                dataTestId="resultsNumber"
              />
            </TextV2>
          </Grid>
          <Grid size={{ xs: 12, md: 'auto' }}>
            <SearchBox
              placeholderKey="ui.event-management.group.search-results.placeholder"
              value={searchText}
              onChange={onSearchTextChanges}
            />
          </Grid>
        </Grid>
        <Grid>
          {pagedItems.length && pageCount > 1 ? (
            <PaginationControls
              itemsPerPage={9}
              show
              paginator={paginator}
              translations={{
                nextLabel: t('ui.common.pagination.next'),
                prevLabel: t('ui.common.pagination.prev'),
              }}
            />
          ) : null}
        </Grid>
      </Box>
      {hasOrgs ? (
        <OrganisationOrGroupList
          organisationsOrGroups={pagedItems}
          group={group}
        />
      ) : (
        <TextV2 color="textSecondary">
          {t('ui.event-management.group.search-results.no-organisations')}
        </TextV2>
      )}
    </Container>
  );
};

export default GroupOrganisationList;
